import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { LoadingStatus } from "../components/Shared/Modals/LoadingStatusModal/LoadingStatusModal";
import {
  OrganisationDataQueryFn,
  organisationDataQueryKey,
  organisationDataQueryOptions
} from "../config/queryConfig";
import { useLoadingStatus } from "./useLoadingStatus";
import { VenueGroupConfig } from "../types/VenueGroup";
import { VenueUserConfig } from "../types/VenueUser";
import { useAppSelector } from "../store/hooks";
import { selectCollinsUser } from "../store/slices/sessionSlice";

type OrganisationDataQueryResponse = {
  user?: VenueUserConfig;
  venueGroup?: VenueGroupConfig;
};

const toOrganisationData = (
  data: unknown
): OrganisationDataQueryResponse | undefined =>
  typeof data === "object" && data !== null && !Array.isArray(data)
    ? (data as OrganisationDataQueryResponse)
    : undefined;

/**
 * Loads organisation data once startup has completed.
 *
 * The query is gated on `enabled` (startup `status === "success"`) rather than
 * on the query key, so the cached entry survives the transition to success and
 * is reused instead of refetched. Timing defaults come from
 * `organisationDataQueryOptions`; the key is scoped to the Collins user.
 */
export const useOrganisationDataQuery = (
  status: LoadingStatus,
  loadOrganisationData: OrganisationDataQueryFn
) => {
  const queryClient = useQueryClient();
  const { updateStage } = useLoadingStatus();
  const user = useAppSelector(selectCollinsUser);
  const queryKey = organisationDataQueryKey(user);

  const query = useQuery({
    ...organisationDataQueryOptions,
    queryKey,
    queryFn: loadOrganisationData,
    enabled: status === "success",
    // Seed the first render from the persisted-then-restored cache so a
    // returning user's organisation data paints immediately instead of a
    // loading state. An empty cache (first-ever load) yields undefined,
    // preserving the genuine loading path.
    initialData: () => queryClient.getQueryData(queryKey),
    // Treat the scoped snapshot as stale so the query is revalidated rather
    // than waiting up to staleTime after restore.
    // When the persister has already restored the entry, initialData is not
    // used, so the snapshot is invalidated on restore in queryClient.ts.
    initialDataUpdatedAt: 0
  });

  useEffect(() => {
    if (status !== "success" || !toOrganisationData(query.data)) return;
    updateStage("organisation", "success");
  }, [status, query.data, updateStage]);

  return query;
};
